import React, { useMemo } from 'react';
import cn from 'classnames';
import { Typography, WithInfoIcon } from 'seagull';
import { PaymentTerms } from 'seagull/icons';
import { useResponsive } from "../../contexts";
import { Service } from "./Service";
import styles from "./ReviewServices.module.scss";
import { BillingGroupTypes, summarize } from "../genServicesSummary";
import { relationshipModel, timeModel } from "../../../models";
import { serviceModel } from "../../../models";
import { utils } from "../../../utils";
import BillingSchedule from "../components/BillingSchedule";
import { useFeatureFlags } from "../../../hooks/useFeatureFlags";
export function ReviewServices(props) {
  const {
    relationship,
    selectedBundleID,
    selectedOptionalServices,
    setSelectedOptionalServices
  } = props;
  const {
    isMobile
  } = useResponsive();
  const {
    CUSTOM_BRANDING
  } = useFeatureFlags();
  const effectiveDate = relationshipModel.effectiveDate(relationship);
  const [services, nonOptionalServices, optionalServices] = useMemo(() => {
    const services = relationshipModel.actualServices(relationship, selectedBundleID);
    return [services, services.filter(s => s.participation.type === 'mandatory'), services.filter(s => s.participation.type === 'optional')];
  }, [relationship, selectedBundleID]);
  const billingSummary = useMemo(() => services.reduce(summarize({
    selectedOptionalServices: selectedOptionalServices || [],
    effectiveDate,
    now: new Date(),
    billingFrequencyOnly: true
  }), {}), [services, selectedOptionalServices, effectiveDate]);
  return /*#__PURE__*/React.createElement("div", {
    className: styles.root
  }, services.length > 1 && !services.map(serviceModel.cost).some(cost => serviceModel.isVariablePrice(cost) || serviceModel.isRangePrice(cost)) ? /*#__PURE__*/React.createElement("section", {
    "data-testid": "billing-summary",
    className: styles.section
  }, [BillingGroupTypes.Once, BillingGroupTypes.Weekly, BillingGroupTypes.BiWeekly, BillingGroupTypes.Monthly, BillingGroupTypes.Quarterly, BillingGroupTypes.Yearly].flatMap(groupType => {
    return [false].map(variablePrice => {
      var _billingSummary$group;
      const group = (_billingSummary$group = billingSummary[groupType]) === null || _billingSummary$group === void 0 ? void 0 : _billingSummary$group.get(variablePrice);
      return group != null ? /*#__PURE__*/React.createElement("div", {
        key: "".concat(groupType, "-").concat(variablePrice),
        className: styles.billingSchedule,
        "data-testid": "billing-schedule-service-summary"
      }, /*#__PURE__*/React.createElement(Typography, {
        variant: "paragraph1",
        noWrap: true,
        className: cn(styles.coverLetter, {
          [styles.mobile]: isMobile
        })
      }, /*#__PURE__*/React.createElement(Typography, {
        as: "span"
      }, "Billed ", groupType, groupType === BillingGroupTypes.OnEffectiveDate ? " (".concat(timeModel.formatDate(effectiveDate), ")") : '', ' ', "-", ' '), /*#__PURE__*/React.createElement(Typography, {
        as: "span",
        weight: "bold"
      }, utils.formatDollarAmount(group.totalAmount)))) : null;
    });
  })) : null, /*#__PURE__*/React.createElement("section", {
    className: styles.section
  }, /*#__PURE__*/React.createElement("div", {
    className: cn(styles.sectionTitle, {
      [styles.mobile]: isMobile
    })
  }, !CUSTOM_BRANDING && /*#__PURE__*/React.createElement(PaymentTerms, null), /*#__PURE__*/React.createElement(Typography, {
    variant: "h6",
    weight: "normal"
  }, "Services")), services.length > 0 ? /*#__PURE__*/React.createElement("ul", null, nonOptionalServices.map((service, i) => /*#__PURE__*/React.createElement("li", {
    key: i,
    className: styles.serviceItem
  }, /*#__PURE__*/React.createElement(Service, {
    idx: i,
    relationship: relationship,
    service: service
  }))), !!optionalServices.length && /*#__PURE__*/React.createElement(WithInfoIcon, {
    as: "li",
    className: styles.serviceItem,
    info: "Services in this section are optional. Only the services selected will be added to your agreement"
  }, /*#__PURE__*/React.createElement(Typography, null, "Additional services to choose from")), optionalServices.map((service, i) => /*#__PURE__*/React.createElement("li", {
    key: i,
    className: styles.serviceItem
  }, /*#__PURE__*/React.createElement(Service, {
    idx: i + nonOptionalServices.length,
    relationship: relationship,
    service: service,
    optionallySelected: selectedOptionalServices.includes(service.id),
    onChangeOptionallySelected: selected => {
      const newSelected = selected ? selectedOptionalServices.concat(service.id) : selectedOptionalServices.filter(id => id !== service.id);
      setSelectedOptionalServices(newSelected);
    }
  }))), /*#__PURE__*/React.createElement(BillingSchedule, {
    relationship: relationship,
    selectedBundleID: selectedBundleID,
    selectedOptionalServices: selectedOptionalServices
  })) : /*#__PURE__*/React.createElement("div", {
    className: styles.agreementPreviewNoServices
  }, /*#__PURE__*/React.createElement(Typography, {
    skin: "secondary"
  }, "Once a package is selected, sevices will appear here"))));
}